import React, { useEffect } from 'react'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import TableFooter from '@mui/material/TableFooter';
import { useDispatch, useSelector } from 'react-redux';
import { get_company_users, setPage } from '../../../store/account/accountSlice';
import TablePagination from '@mui/material/TablePagination';
import { ACCOUNT_LIMIT, roles, SUPER_ADMIN } from '../../../helpers/constants';
import TablePaginationActions from '../TablePaginationActions';

const columns = [
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'contact_no', label: 'Mobile No' },
  { id: 'role', label: 'Role' },
  { id: 'is_active', label: 'Status' },
  { id: 'action', label: 'Actions' }
];

const AccountTable = (props) => {
  const { is_loading,
    total_users_count,
    handleActivateDeactivateUsers,
    is_account_modal_open,
    handleAccountUpdateModal
  } = props;
  const { company_users, page } = useSelector((state) => state.account);
  const dispatch = useDispatch();
  const rowsPerPage = ACCOUNT_LIMIT

  const handleStatusChange = async (row) => {
    let newStatus = row.is_active ? 'dc' : 'ac';
    await handleActivateDeactivateUsers({ user_id: row.id, updated_data: newStatus });
  }

  const handleModal = async (c_action, user_id) => {
    await handleAccountUpdateModal(!is_account_modal_open, c_action, user_id);
  };

  useEffect(() => {
    dispatch(get_company_users({ page: 1, limit: ACCOUNT_LIMIT }));
  }, [dispatch]);

  const handlePageChange = (event, newPage) => {
    dispatch(setPage(newPage));
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer
        sx={{
          maxHeight: { xl: 800, md: 440 },
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "4px",
          },
        }}
      >
        <Table stickyHeader aria-label="sticky table"
          sx={{
            "& .MuiTableCell-stickyHeader": {
              backgroundColor: "#f5f5f5",
              fontWeight: "bold",
              zIndex: 2,
            },
          }}
        >
          <TableHead>
            <TableRow sx={{
              '& .MuiTableCell-root:not(:first-of-type)': {
                textAlign: 'left',
              }
            }}>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#eeeeee70",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {
              company_users.length > 0 ?
                company_users
                  .map((row, idx) => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={idx}>
                        <TableCell align="left">
                          <Tooltip title={row.first_name && row.last_name ? `${row.first_name} ${row.last_name}` : "-"} arrow>
                            <Stack display='flex' flexDirection='row' alignItems='left'>
                              <Box sx={{
                                width: '2rem',
                                height: '2rem',
                                mr: 1,
                                bgcolor: theme => theme.palette.primary.main,
                                borderRadius: '50px'
                              }}>
                                <Box sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center'
                                }}
                                  color='white'
                                  letterSpacing={1}
                                  textTransform='uppercase'
                                >  {row.first_name && row.last_name
                                  ? `${row.first_name.charAt(0)}${row.last_name.charAt(0)}`
                                  : ''}</Box>
                              </Box>
                              {row.first_name && row.last_name ? `${row.first_name} ${row.last_name}` : '-'}
                            </Stack>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="left">
                          <Tooltip title={row.email || "-"} arrow>
                            {row.email}
                          </Tooltip>
                        </TableCell>
                        <TableCell align="left">
                          <Tooltip title={row.contact_no || "-"} arrow>
                            {row.contact_no}
                          </Tooltip>
                        </TableCell>
                        <TableCell align="left">
                          <Tooltip title={Number(row.role) === SUPER_ADMIN ? 'SUPER_ADMIN' : (roles.find(r => r.id === Number(row.role))?.name || row.role || "-")} arrow>
                            {Number(row.role) === SUPER_ADMIN ? 'SUPER_ADMIN' : (roles.find(r => r.id === Number(row.role))?.name || row.role)}
                          </Tooltip>
                        </TableCell>
                        <TableCell align="left">
                          <Tooltip title={row.is_active ? 'Active' : 'Inactive' || "-"} arrow>
                            {row.is_active ? 'Active' : 'Inactive'}
                          </Tooltip>
                        </TableCell>
                        <TableCell align="left">
                          <Stack direction="row" justifyContent='left'>
                            <Tooltip title="Edit" arrow>
                              <IconButton type="button" aria-label="edit" color='primary' onClick={() => handleModal("UPDATE", row.id)}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.5rem' }}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                </svg>

                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Resend Activation Link">
                              <IconButton sx={{ position: 'relative' }} type="button" aria-label="edit" color='primary' onClick={() => handleModal("UPDATE", row.id)}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '25px' }}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                                </svg>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '0.75rem', position: 'absolute' }}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                </svg>

                              </IconButton>
                            </Tooltip>
                            <Tooltip title={row.is_active ? 'Active' : 'Inactive'}>
                              <IconButton
                                type="button"
                                aria-label="toggle-status"
                                color="primary"
                                onClick={() => handleStatusChange(row)}
                              >
                                {row.is_active ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }) :
                !is_loading &&
                  company_users.length === 0 ? (
                  <TableRow>
                    <TableCell align='center' colSpan={8}>
                      No Record Found
                    </TableCell>
                  </TableRow>) :
                  (<TableRow>
                    <TableCell align='center' colSpan={8}>
                      loading....
                    </TableCell>
                  </TableRow>
                  )
            }
          </TableBody>
          {company_users.length > 0 && (
            <TableFooter>
              <TableRow >
                <TableCell colSpan={6} sx={{ padding: 0 }}>
                  <TablePagination
                    component="div"
                    count={total_users_count}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[]}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AccountTable;
